import { useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hammer, Loader2, Database, Cpu, Shield, Layers, Boxes } from "lucide-react";
import { toast } from "sonner";

export default function BuildPlanner() {
  const location = useLocation();
  const [desc, setDesc] = useState(location.state?.description || "");
  const [target, setTarget] = useState("");
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!desc.trim()) { toast("Describe what you want to build"); return; }
    setLoading(true); setPlan(null);
    try {
      const res = await api.post("/ai/build-plan", { description: desc, target_customer: target });
      setPlan(res.data);
      toast.success("Build plan ready");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-center mb-10">
        <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4"><Hammer className="w-6 h-6 text-emerald-400" /></div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50">Build My MVP</h1>
        <p className="text-slate-400 mt-2 max-w-xl mx-auto">Describe your product and get a lean, phased build plan so you don't over-scope on day one.</p>
      </div>

      <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 mb-8 space-y-4">
        <div><Label className="text-slate-400 text-xs mb-1.5 block">What do you want to build?</Label>
          <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="e.g. A scheduling and deposit tool for tattoo artists" className="bg-white/5 border-white/10 text-slate-200 min-h-24" data-testid="build-desc-input" /></div>
        <div><Label className="text-slate-400 text-xs mb-1.5 block">Target customer (optional)</Label>
          <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. Independent tattoo artists" className="bg-white/5 border-white/10 text-slate-200" /></div>
        <Button type="submit" disabled={loading} className="w-full h-11 bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold" data-testid="build-plan-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Planning your MVP…</> : <><Hammer className="w-4 h-4 mr-2" />Generate Build Plan</>}
        </Button>
      </form>

      {plan && (
        <div className="space-y-6 fade-up">
          <div className="grid md:grid-cols-3 gap-4">
            {["mvp", "v2", "v3"].map((phase, i) => (
              <div key={phase} className="glass rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center font-mono text-sm font-bold ${["bg-emerald-500/20 text-emerald-400", "bg-cyan-500/20 text-cyan-400", "bg-indigo-500/20 text-indigo-400"][i]}`}>{i + 1}</span>
                  <h4 className="font-semibold text-slate-100 uppercase">{phase}</h4>
                </div>
                <ul className="space-y-1.5">{(plan.phases?.[phase] || []).map((f, j) => <li key={j} className="text-sm text-slate-300 flex gap-2"><span className="text-cyan-500 mt-1">▸</span>{f}</li>)}</ul>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card icon={Layers} title="Architecture"><p className="text-sm text-slate-300">{plan.architecture}</p></Card>
            <Card icon={Cpu} title="Complexity"><p className="text-sm text-slate-300">{plan.complexity}</p></Card>
            <Card icon={Boxes} title="Recommended Stack">
              {plan.recommended_stack && Object.entries(plan.recommended_stack).map(([k, v]) => <p key={k} className="text-sm text-slate-300"><span className="text-slate-500 capitalize">{k}: </span>{v}</p>)}
            </Card>
            <Card icon={Database} title="Database Entities"><Tags items={plan.database_entities} /></Card>
            <Card icon={Shield} title="Security"><Bullets items={plan.security} /></Card>
            <Card icon={Cpu} title="AI Capabilities"><Bullets items={plan.ai_capabilities} /></Card>
            <Card icon={Boxes} title="Integrations"><Tags items={plan.integrations} /></Card>
            <Card icon={Layers} title="User Roles"><Tags items={plan.user_roles} /></Card>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return <div className="glass rounded-xl p-5"><div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-cyan-400" /><h4 className="font-semibold text-slate-100 text-sm">{title}</h4></div>{children}</div>;
}
function Tags({ items }) { return <div className="flex flex-wrap gap-2">{(items || []).map((t, i) => <span key={i} className="text-xs font-mono px-2 py-1 rounded bg-white/5 border border-white/8 text-slate-300">{t}</span>)}</div>; }
function Bullets({ items }) { return <ul className="space-y-1.5">{(items || []).map((t, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-cyan-500 mt-1">▸</span>{t}</li>)}</ul>; }
